import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { Suspense, useEffect } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import {
  LayoutGrid, Users, Home, CheckSquare, CreditCard, Trophy,
  Layers, Calendar, Package, FileText, BarChart3, Clock,
  GraduationCap, UserPlus, AlertTriangle, Link2, Settings, MapPin, PartyPopper, LineChart,
} from 'lucide-react'
import { useAuth } from '../contexts/AuthContext'
import { useTenant } from '../contexts/TenantContext'
import { useLocationFilter } from '../contexts/LocationFilterContext'
import { AdminLayout } from './AdminLayout'
import { NavItem } from './AdminNavigation'
import { Spinner } from './Spinner'
import logo from '../assets/El CapIT_logo_top_left.png'

const navigation: NavItem[] = [
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: <LayoutGrid className="w-5 h-5" /> },
  { id: 'members', label: 'Members', path: '/members', icon: <Users className="w-5 h-5" /> },
  { id: 'households', label: 'Families', path: '/households', icon: <Home className="w-5 h-5" /> },
  { id: 'check-in', label: 'Check-In', path: '/check-in', icon: <CheckSquare className="w-5 h-5" /> },
  { id: 'pos', label: 'POS', path: '/pos', icon: <CreditCard className="w-5 h-5" /> },
  { id: 'competitions', label: 'Competitions', path: '/competitions', icon: <Trophy className="w-5 h-5" /> },
  { id: 'routes', label: 'Routes', path: '/routes', icon: <Layers className="w-5 h-5" /> },
  { id: 'bookings', label: 'Bookings', path: '/bookings', icon: <Calendar className="w-5 h-5" /> },
  { id: 'inventory', label: 'Inventory', path: '/inventory', icon: <Package className="w-5 h-5" /> },
  { id: 'purchase-orders', label: 'Purchase Orders', path: '/purchase-orders', icon: <FileText className="w-5 h-5" /> },
  { id: 'reports', label: 'Reports', path: '/reports', icon: <BarChart3 className="w-5 h-5" /> },
  { id: 'analytics', label: 'Analytics', path: '/analytics', icon: <LineChart className="w-5 h-5" /> },
  { id: 'timeclock', label: 'Time Clock', path: '/timeclock', newTab: true, icon: <Clock className="w-5 h-5" /> },
  { id: 'youth-programs', label: 'Youth Programs', path: '/youth-programs', icon: <GraduationCap className="w-5 h-5" /> },
  { id: 'private-events', label: 'Private Events', path: '/private-events', icon: <PartyPopper className="w-5 h-5" /> },
  { id: 'leads', label: 'Leads', path: '/leads', icon: <UserPlus className="w-5 h-5" /> },
  { id: 'incidents', label: 'Incidents', path: '/incidents', icon: <AlertTriangle className="w-5 h-5" /> },
  { id: 'integrations', label: 'Integrations', path: '/integrations', icon: <Link2 className="w-5 h-5" /> },
  { id: 'admin', label: 'Admin', path: '/admin', icon: <Settings className="w-5 h-5" /> },
]

const NAV_BY_ROLE: Record<string, string[]> = {
  administrator: [
    'dashboard', 'members', 'households', 'check-in', 'pos', 'bookings', 'competitions', 'routes', 'inventory',
    'purchase-orders', 'reports', 'analytics', 'leads', 'incidents', 'timeclock', 'youth-programs', 'private-events', 'integrations', 'admin',
  ],
  manager: [
    'dashboard', 'members', 'households', 'check-in', 'pos', 'bookings', 'competitions', 'routes', 'inventory',
    'purchase-orders', 'reports', 'analytics', 'leads', 'incidents', 'timeclock', 'youth-programs', 'private-events', 'integrations', 'admin',
  ],
  'front-desk': [
    'dashboard', 'members', 'check-in', 'pos', 'bookings', 'routes', 'inventory',
  ],
  instructor: [
    'dashboard', 'check-in', 'bookings', 'routes',
  ],
}

const pageTransition = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.25, ease: [0.16, 1, 0.3, 1] } },
  exit: { opacity: 0, transition: { duration: 0.15 } },
}

export default function Layout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout, role } = useAuth()
  const { tenant, refreshFeatures } = useTenant()
  const { allowedLocations, activeLocationId, setActiveLocationId, canSeeAllLocations } = useLocationFilter()
  const isGymSubdomain = window.location.hostname.split('.').length >= 3
  const gymLogo = isGymSubdomain ? tenant?.branding?.logoUrl : null

  useEffect(() => {
    if (user) refreshFeatures()
  }, [user?.username])

  const allowedIds = role ? NAV_BY_ROLE[role] : undefined
  let activeNav = allowedIds
    ? navigation.filter(item => allowedIds.includes(item.id))
    : navigation

  if (tenant?.features) {
    activeNav = activeNav.filter(item => tenant.features!.includes(item.id))
  }

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const handleNavigate = (path: string) => {
    navigate(path)
  }

  const locationSelector = allowedLocations.length > 0 ? (
    <div className="flex items-center gap-2">
      <MapPin className="w-4 h-4 text-neutral-400" />
      <select
        value={activeLocationId}
        onChange={e => setActiveLocationId(e.target.value)}
        className="text-sm border border-neutral-200 dark:border-neutral-700 rounded-lg px-3 py-1.5 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 focus:outline-none focus:ring-2 focus:ring-primary-500"
      >
        {canSeeAllLocations && <option value="">All Locations</option>}
        {allowedLocations.map(loc => (
          <option key={loc.id} value={loc.id}>{loc.name}</option>
        ))}
      </select>
    </div>
  ) : null

  return (
    <AdminLayout
      navItems={activeNav}
      activePath={location.pathname}
      logo={
        gymLogo
          ? <img src={gymLogo} alt={tenant?.branding?.gymName || 'Gym'} className="h-10 max-w-[160px] object-contain" />
          : <img src={logo} alt="El CapIT" className="h-12" />
      }
      userName={user?.attributes?.email || user?.username || 'User'}
      headerContent={locationSelector}
      onLogout={handleLogout}
      onNavigate={handleNavigate}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={location.pathname}
          {...pageTransition}
        >
          <Suspense fallback={
            <div className="flex items-center justify-center min-h-[60vh]">
              <Spinner size="lg" />
            </div>
          }>
            <Outlet />
          </Suspense>
        </motion.div>
      </AnimatePresence>
    </AdminLayout>
  )
}
